/** Running cost (Feature 45): one run's cost, a period's rollups, and the rate card. */
import { apiGet, apiGetBlob, apiGetWithHeaders, apiPost, type BlobResult } from '../client.js';
import type {
  CostChoices,
  CostEvent,
  CostEventPage,
  FxRateBody,
  FxRates,
  OrganizationCost,
  Payer,
  PeriodCost,
  RateCard,
  RateRowBody,
  ReconciliationRow,
  RepriceBody,
  RepriceResult,
  Subscription,
  SubscriptionBody,
  TaskCost,
} from '../types.js';

/** A run's priced and collected lines; 404 when the task recorded no usage. */
export function getTaskCost(taskId: string): Promise<TaskCost> {
  return apiGet<TaskCost>(`/api/v1/tasks/${taskId}/cost`);
}

export interface CostRollupsFilters {
  organization_id: string;
  /** ISO dates; the period is half-open, `start <= day < end`. */
  start: string;
  end: string;
}

export function getCostRollups(filters: CostRollupsFilters): Promise<PeriodCost> {
  return apiGet<PeriodCost>('/api/v1/admin/cost/rollups', { ...filters });
}

/** ISO dates, half-open; what the fallback labels and rated counts describe. */
export interface CostPeriod {
  start: string;
  end: string;
}

/** The rate card; the unpriced pairs are labeled for the period, the current month by default. */
export function getRateCard(period?: CostPeriod): Promise<RateCard> {
  return apiGet<RateCard>('/api/v1/admin/cost/rates', period ? { ...period } : undefined);
}

/** Every usage type, unit, and resource the rate card form can offer; declared first, then seen. */
export function getCostChoices(): Promise<CostChoices> {
  return apiGet<CostChoices>('/api/v1/admin/cost/choices');
}

/** Receipts against the rate card per resource for the period, computed on read. */
export function getReconciliation(period: CostPeriod): Promise<ReconciliationRow[]> {
  return apiGet<ReconciliationRow[]>('/api/v1/admin/cost/reconciliation', { ...period });
}

export function addRateRow(body: RateRowBody): Promise<RateCard> {
  return apiPost<RateCard>('/api/v1/admin/cost/rates', body);
}

export function getFxRates(): Promise<FxRates> {
  return apiGet<FxRates>('/api/v1/admin/cost/fx');
}

export function addFxRate(body: FxRateBody): Promise<FxRates> {
  return apiPost<FxRates>('/api/v1/admin/cost/fx', body);
}

export function listSubscriptions(): Promise<Subscription[]> {
  return apiGet<Subscription[]>('/api/v1/admin/cost/subscriptions');
}

export function addSubscription(body: SubscriptionBody): Promise<Subscription> {
  return apiPost<Subscription>('/api/v1/admin/cost/subscriptions', body);
}

/** Replay the pass over every organization, a month at a time; one row per pass (Feature 68). */
export function reprice(body: RepriceBody): Promise<RepriceResult> {
  return apiPost<RepriceResult>('/api/v1/admin/cost/reprice', body);
}

const TOTAL_COUNT_HEADER = 'X-Total-Count';

export interface ListCostEventsFilters {
  /** ISO-8601 datetimes; the period is half-open on the event's `at`. */
  start: string;
  end: string;
  organization_id?: string;
  capability_id?: string;
  usage_type?: string;
  payer?: Payer;
  subject_kind?: 'task' | 'ingestion_job';
  limit?: number;
  offset?: number;
}

/**
 * One page of the platform ledger with its total, for a pager. A server
 * that sends no total yields the page length, as the task list does.
 */
export async function listCostEventsPage(filters: ListCostEventsFilters): Promise<CostEventPage> {
  const params = Object.fromEntries(
    Object.entries(filters).filter(([, value]) => value !== undefined),
  );
  const { body, headers } = await apiGetWithHeaders<CostEvent[]>(
    '/api/v1/admin/cost/events',
    params,
  );
  const total = Number.parseInt(headers.get(TOTAL_COUNT_HEADER) ?? '', 10);
  return { items: body, total: Number.isNaN(total) ? body.length : total };
}

/** Every organization with usage in the period; ISO dates, half-open. */
export function getCostSummary(filters: {
  start: string;
  end: string;
}): Promise<OrganizationCost[]> {
  return apiGet<OrganizationCost[]>('/api/v1/admin/cost/summary', { ...filters });
}

/** One column of the cost export (Feature 69): the name the route takes and the header the file shows. */
export interface ExportColumn {
  name: string;
  header: string;
}

/** The agent's catalog, in its order; `quantities` expands to a column per unit. */
export const EXPORT_COLUMNS: readonly ExportColumn[] = [
  { name: 'at', header: 'When' },
  { name: 'organization', header: 'Organization' },
  { name: 'organization_id', header: 'Organization id' },
  { name: 'subject_kind', header: 'Subject kind' },
  { name: 'subject_id', header: 'Task' },
  { name: 'agent_id', header: 'Agent' },
  { name: 'capability_id', header: 'Capability' },
  { name: 'usage_type', header: 'Type' },
  { name: 'resource', header: 'Resource' },
  { name: 'payer', header: 'Payer' },
  { name: 'quantities', header: 'Quantities' },
  { name: 'estimated', header: 'Estimated' },
  { name: 'declared', header: 'Declared' },
  { name: 'reported_cost_amount', header: 'Receipt' },
  { name: 'reported_cost_currency', header: 'Receipt currency' },
  { name: 'priced_amount', header: 'Cost (THB)' },
  { name: 'status', header: 'Status' },
  { name: 'reason', header: 'Reason' },
  { name: 'line_count', header: 'Lines' },
  { name: 'id', header: 'Event id' },
];

/** The set ticked on a fresh browser. */
export const DEFAULT_EXPORT_COLUMNS: readonly string[] = [
  'at',
  'organization',
  'capability_id',
  'usage_type',
  'resource',
  'priced_amount',
  'status',
];

export interface ExportCostFilters {
  /** ISO dates, half-open. */
  start: string;
  end: string;
  organization_id?: string;
  capability_id?: string;
  usage_type?: string;
  payer?: Payer;
  subject_kind?: 'task' | 'ingestion_job';
}

/** One CSV of every usage event under the filters; the agent names the file. */
export function exportCostEvents(
  filters: ExportCostFilters,
  columns: readonly string[],
): Promise<BlobResult> {
  return apiGetBlob(
    '/api/v1/admin/cost/export',
    { ...filters, columns: columns.join(',') },
    'cost-events.csv',
  );
}

/** One CSV of a task's usage events, the same structure. */
export function exportTaskCostEvents(
  taskId: string,
  columns: readonly string[],
): Promise<BlobResult> {
  return apiGetBlob(
    `/api/v1/tasks/${taskId}/cost/export`,
    { columns: columns.join(',') },
    `cost-events-task-${taskId.slice(0, 8)}.csv`,
  );
}
